// SceneryRegions — Set13 §3.4: the `MapObject → SceneryRegion` bridge, mirroring the material
// system's MaterialRegions.ts.
//
// ANTI-DRIFT RULE (the Set1 B3/B4 bug class): `SceneryRegion` HOLDS its source
// `PathSceneryFeature` BY REFERENCE rather than copying its fields — a new field on the feature
// needs NO bridge edit (read it from `region.pathFeature`). The ONLY fields the bridge resolves
// (and therefore owns) are: id, polylines, closed, bbox, resolved density (spacing/presence),
// the RESOLVED placement seed + kind-seed (`seedOverride`→`seed`, `kindSeedOverride`→`kindSeed`),
// RESOLVED dominance + the resolved suppression fraction (`PathSceneryFeature.SuppressionOf`).
//
// ⚠ A region is a PREDICATE, never a list (§3.4). `SceneryRegion` exposes `geoFactorAt(p)` and a
// bbox. It must NEVER expose "the instances in this region": a Texas-sized fill would be ~19
// billion instances. Any code path that iterates instances over a region's area is the crash.
// This file deliberately exposes NO instance-enumerating API (a structural guard, tested).
//
// Placement uses a jittered grid (§3.3): per-candidate-cell presence gate + O(1) hashing. The
// `densityAt(p)` here is the REGION's contribution — the world-space fbm field (glades/stands/edge
// thinning) and the mask factors live in the PLACER (they need the resolved kind + masks), so this
// bridge resolves only the region-geometry-dependent part: inside/edge distance → a presence
// multiplier.
import type {MapObject_ForTerrain} from "../../Heights/Poisson/@Shared/ConstraintRasterizer/MapObject_ForTerrain.js";
import type {Pt} from "../../Heights/Poisson/@Shared/ConstraintRasterizer/RasterFeatures_Pure.js";
import {pointInPolygon} from "../../Heights/Poisson/@Shared/ConstraintRasterizer.js";
import {buildRegionDistanceField, distToPolyline, sampleRegionDistanceAt, type RegionDistanceField} from "../../Materials/Splat/MaterialRegions.js";
import {resolveRegionDominances} from "../../Materials/Splat/RegionDominance.js";
import {PathSceneryFeature, SceneryKindEntry} from "../@SceneryFeature.js";
import {Rect} from "../../../Utils/ThreeJS/VectorStructs.js";
import {resolveNumber} from "../SceneryHash.js";
import {timeIt} from "../Render/SceneryTiming.js";

/** A scenery region: one active `PathSceneryFeature` + the bridge-resolved geometry. All
 *  authoring fields (kinds, mode, strokeWidth, feather, spacingOverride, presenceOverride,
 *  dominance, suppressPriorScenery, masks) are read from `pathFeature` — NEVER copied up (see
 *  file docblock). */
export interface SceneryRegion {
	/** path_id (stable; drives RNG seeding). Falls back to `${objID}_path${index}` for legacy
	 *  paths with no path_id (deterministic — obj ids are stable). */
	id: string;
	/** The authored source, verbatim. */
	pathFeature: PathSceneryFeature;
	/** World-space polylines from `_meshSet_world`. */
	polylines: Pt[][];
	/** `_curve_closed_resolved` (AUTHORITATIVE — NOT `path.curve_closed`; inheritance can flip it). */
	closed: boolean;
	/** Broad-phase bbox over all polylines, inflated by `feather/2 + strokeWidth/2` (the region
	 *  geometry's max reach — strokeWidth is the FULL width, so its radius is half) — the placer's
	 *  culling gate. */
	bbox: Rect;
	/** RESOLVED dominance: `pathFeature.dominance ?? derived containment depth` (§3.4, computed among SCENERY regions only — the one field that legitimately shadows the feature). Consulted ONLY by suppression: a suppressor thins strictly-LOWER-dominance regions over its area. */
	dominance: number;
	/** RESOLVED suppression fraction (0..1; `PathSceneryFeature.SuppressionOf`) — re-exposed so the pure placer's structural `PlacementRegionLike` reads it without the full bridge shape. */
	suppressPriorScenery: number;
	/** Region density knobs from `pathFeature` (the "resolved density (spacing/presence)" the
	 *  bridge owns — Set13 §3.3). Absent (`undefined`/null) = use the kind's default. The placer
	 *  folds them into its per-region density at the cell centre (see `densityAt`). Exposed here
	 *  (not just via the held `pathFeature`) so the PURE placer's structural `PlacementRegionLike`
	 *  can read them without depending on the full bridge shape. */
	presenceOverride?: number|n;
	spacingOverride?: number|n;
	/** RESOLVED placement seed (Set17 §2): `pathFeature.seedOverride` ?? 0. The placer adds it to
	 *  the region-id seed so a NON-ZERO override EXCLUSIVELY perturbs THIS region's RNG streams
	 *  (presence/jitter/rank/kind-draw) without touching its geometry or density — the region
	 *  "reroll" knob. Absent/0 keeps the pure id-based seed (unchanged behaviour). Re-exposed
	 *  here (not just via the held `pathFeature`) so the PURE placer's structural
	 *  `PlacementRegionLike` reads it without the full bridge shape. */
	seed: number;
	/** RESOLVED kind-seed (kindSeedOverride, OPTIONAL — absent vs 0 are DISTINCT): WHEN SET it
	 *  REPLACES `seed` for the mixed-kind draw only, so a good distribution is kept while re-rolling
	 *  which kind owns each cell. Absent → kind draw follows `seed`. Re-exposed for the pure placer. */
	kindSeed?: number|n;
	/** Set16 — the NORMALISED kind list (drop empty/weight≤0/unresolvable, §1) this region
	 *  distributes over, re-exposed from `pathFeature` (anti-drift: `pathFeature.kinds` is never
	 *  copied up). The placer's structural `PlacementRegionLike` reads this for the per-cell kind
	 *  draw + the shared-density policy (finest sibling lattice / max sibling presence, §2/§3). */
	kinds: SceneryKindEntry[];
	/** Resolved presence multiplier from region geometry at (x, y): the kind's presence × the
	 *  region's boundary feather ramp × mask factors. Mask factors depend on terrain data the
	 *  bridge does not have (elevation/slope), so `densityAt` here handles ONLY the geometric
	 *  part; the PLACER multiplies in mask + fbm. See {@link regionGeoFactorAt}. */
	geoFactorAt: (x: number, y: number) => number;
	/** Pre-built distance field (built once at collect time — the geometry is hoisted + cached by
	 *  the material system's `buildRegionDistanceField`, so repeated `geoFactorAt` samples are
	 *  accelerated grid queries instead of brute-force edge scans). Absent for legacy regions. */
	distanceField?: RegionDistanceField;
}

/** Collect the scenery regions from packaged terrain objects: every path with an ACTIVE `sceneryFeature` (`!disabled && (kinds non-empty post-NormalizeKinds || suppressPriorScenery > 0` — a SUPPRESSION-ONLY entry places nothing itself; its region has empty `kinds`). Drops paths with no usable world geometry, and `mode: "fill"`/`"fillAndStroke"` on an OPEN path (warned, dropped). `resolvable` is the caller's catalogue-id set (drops unresolvable kind ids). Output order: deterministic by id. */
export function collectSceneryRegions(objects: ReadonlyArray<MapObject_ForTerrain>, resolvable?: ReadonlySet<string>|n): SceneryRegion[] {
	const regions: SceneryRegion[] = [];
	for (const obj of objects) {
		for (const [index, path] of (obj.paths ?? []).entries()) {
			const sf = path.sceneryFeature;
			if (!PathSceneryFeature.IsActive(sf, resolvable)) continue;
			const kinds = PathSceneryFeature.NormalizeKinds(sf, resolvable);
			const suppress = PathSceneryFeature.SuppressionOf(sf);
			if (kinds.length == 0 && suppress <= 0) continue; // paranoia: IsActive already covers this
			const polylines = (path._meshSet_world ?? []).filter(poly=>poly.length >= 2);
			if (polylines.length == 0) continue;
			const closed = !!path._curve_closed_resolved;
			const mode = sf.mode;
			if ((mode == "fill" || mode == "fillAndStroke") && !closed) {
				console.warn(`[collectSceneryRegions] path "${path.path_id ?? `${obj.id}/path${index}`}" has sceneryFeature mode "${mode}" but is not closed — no interior exists; region dropped (Set13 §3.4).`);
				continue;
			}
			// Broad-phase bbox: over all polylines, inflated by strokeWidth/2 + feather/2 (the
			// centered fade's maximum reach — strokeWidth is FULL width, so its radius is half).
			let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
			for (const poly of polylines) {
				for (const p of poly) {
					if (p.x < minX) minX = p.x;
					if (p.y < minY) minY = p.y;
					if (p.x > maxX) maxX = p.x;
					if (p.y > maxY) maxY = p.y;
				}
			}
			const feather = resolveNumber(sf.feather, 8);
			// FULL stroke width (Set25: was half-width) — the geometry extends strokeWidth/2 each
			// side, so the radius used everywhere below is half.
			const strokeWidthRadius = (sf.strokeWidth ?? 0) / 2;
			// Centered fade: density is 0 at `strokeWidthRadius + feather/2` from a polyline (stroke)
			// or `feather/2` past the polygon boundary (fill / fillAndStroke) — the broad-phase gate
			// must cover every point where density > 0.
			const inflate = strokeWidthRadius + feather / 2;
			// PERF (2026-08-28): build the region's distance field ONCE here. `geoFactorAt` samples it
			// via the material system's accelerated grid query instead of the brute-force edge scan
			// (`distToPolyline`/`signedDistanceToPolygon` per sample — the top scenery placement
			// hotspot in the profiler). The field is WeakMap-cached on the stable polyline arrays, so
			// building it here is cheap and the samples are exact (bit-identical to the reference).
			const distanceField = buildRegionDistanceField(mode, polylines, closed);
			regions.push({
				id: path.path_id || `${obj.id}_path${index}`,
				pathFeature: sf,
				polylines,
				closed,
				dominance: 0, // resolved below (needs the whole set)
				suppressPriorScenery: suppress,
				bbox: new Rect(minX - inflate, minY - inflate, (maxX - minX) + inflate * 2, (maxY - minY) + inflate * 2, false),
				presenceOverride: sf.presenceOverride,
				spacingOverride: sf.spacingOverride,
				seed: resolveNumber(sf.seedOverride, 0),
				kindSeed: sf.kindSeedOverride,
				kinds,
				distanceField,
				geoFactorAt: (x, y)=>regionGeoFactorAt(sf, polylines, closed, distanceField, x, y),
			});
		}
	}
	// §3.4 dominance (scenery-only nesting): explicit wins, else containment depth / above-all-fills for strokes. Consulted ONLY by suppression.
	const resolvedDominances = resolveRegionDominances(regions.map(r=>({
		id: r.id, polylines: r.polylines, bbox: r.bbox,
		mode: r.pathFeature.mode, explicitDominance: r.pathFeature.dominance,
	})));
	regions.forEach((r, i)=>r.dominance = resolvedDominances[i]);
	regions.sort((a, b)=>(a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
	return regions;
}

/** The region-geometry contribution to the placement presence at (x, y): 0 outside the region
 *  (plus feather ramp), 1 well inside. Modes are the same three as materials, but the feather is
 *  CENTERED on the region boundary (half behind it, half beyond), so the boundary sits at the 50%
 *  fade point and changing `feather` softens the edge without adding a one-sided apron (count is
 *  roughly invariant):
 *   - "fill": inside the polygon → 1, ramping to 0 across `feather` straddling the closed boundary.
 *   - "stroke": within `strokeWidth/2` metres of the polylines → 1 (the solid core), ramping to
 *     zero across `feather` straddling each outer stroke edge.
 *   - "fillAndStroke": the interior PLUS the stroke apron — one field, the interior distance
 *     shifted by `strokeWidth/2` (mirrors the material region's signed-distance formulation).
 *  Mask factors (elevation/slope) are NOT here — the placer multiplies them in (it has the
 *  terrain lattices). */
export function regionGeoFactorAt(
	sf: PathSceneryFeature,
	polylines: Pt[][],
	closed: boolean,
	distanceField: RegionDistanceField | undefined,
	x: number, y: number,
): number {
	return timeIt("regionGeoFactorAt", ()=>regionGeoFactorAt_inner(sf, polylines, closed, distanceField, x, y));
}

/** The un-timed body of {@link regionGeoFactorAt} (wrapped above for timing). */
function regionGeoFactorAt_inner(
	sf: PathSceneryFeature,
	polylines: Pt[][],
	closed: boolean,
	distanceField: RegionDistanceField | undefined,
	x: number, y: number,
): number {
	const feather = resolveNumber(sf.feather, 8);
	// FULL stroke width (Set25) → the geometric radius is half.
	const strokeWidthRadius = (sf.strokeWidth ?? 0) / 2;
	const mode = sf.mode;

	// PERF (2026-08-28): use the pre-built material-system distance field when available (built at
	// collect time + WeakMap-cached on the polyline arrays) — an accelerated grid query, bit-identical
	// to the brute-force reference. Fall back to the reference scan when no field was built (legacy
	// `SceneryRegion` fixtures that construct `geoFactorAt` directly).
	if (distanceField != null) {
		// `sampleRegionDistanceAt` returns the UNSIGNED min distance for stroke mode and the SIGNED
		// distance (negative inside) for fill/fillAndStroke — the same values the reference computes
		// via `distToPolyline`/`signedDistanceToPolygon`.
		if (mode == "stroke") {
			const d = sampleRegionDistanceAt(distanceField, x, y);
			// feather <= 0 = hard edge at the stroke core's outer edge (the smoothstep band's limit).
			// ⚠ Without this guard, `smoothstep(0, 0, over)` divides by (edge1 - edge0) = 0 → NaN
			// exactly on the region boundary, which silently defeats suppression for that cell.
			// Mirrors the material system's `regionFalloff` hard-edge.
			if (feather <= 0) return d < strokeWidthRadius ? 1 : 0;
			const over = d - (strokeWidthRadius - feather / 2);
			return 1 - smoothstep(0, feather, over);
		}
		const sDist = sampleRegionDistanceAt(distanceField, x, y);
		const shifted = sDist + (mode == "fillAndStroke" ? strokeWidthRadius : 0);
		if (feather <= 0) return shifted < 0 ? 1 : 0;
		const over = shifted + feather / 2;
		return 1 - smoothstep(0, feather, over);
	}

	if (mode == "stroke") {
		// Distance to the nearest polyline; presence is full for the whole solid stroke core (radius
		// `strokeWidthRadius`), then the soft ramp straddles the core's outer edge (radius
		// `strokeWidthRadius - feather/2` → `strokeWidthRadius + feather/2`; 0.5 exactly at the edge).
		let d = Infinity;
		for (const poly of polylines) d = Math.min(d, distToPolyline(x, y, poly, closed));
		if (feather <= 0) return d < strokeWidthRadius ? 1 : 0;
		const over = d - (strokeWidthRadius - feather / 2);
		return 1 - smoothstep(0, feather, over);
	}

	// "fill" / "fillAndStroke": signed distance to the polygon (negative inside).
	let sDist = Infinity;
	for (const poly of polylines) sDist = Math.min(sDist, signedDistanceToPolygon(x, y, poly));
	// Shift the interior boundary outward by strokeWidthRadius (fillAndStroke = interior + apron);
	// the centered ramp then spans `shifted - feather/2` (full) .. `shifted + feather/2` (0), i.e.
	// half the soft edge lies inside the (possibly shifted) boundary and half beyond it.
	const shifted = sDist + (mode == "fillAndStroke" ? strokeWidthRadius : 0);
	if (feather <= 0) return shifted < 0 ? 1 : 0;
	const over = shifted + feather / 2;
	return 1 - smoothstep(0, feather, over);
}

/** Smoothstep ramp (spec-clean: edge0 < edge1). Mirrors the GLSL smoothstep used by the material
 *  feather — 0 at edge0, 1 at edge1, 0/1 clamped outside. */
export function smoothstep(edge0: number, edge1: number, x: number): number {
	const t = Math.max(0, Math.min(1, (x - edge0) / (edge1 - edge0)));
	return t * t * (3 - 2 * t);
}

/** Signed distance to a closed polygon: negative INSIDE (distance to the nearest edge), positive
 *  OUTSIDE. Reuses `pointInPolygon` + `distToPolyline` (closed = the ring wraps). */
function signedDistanceToPolygon(x: number, y: number, poly: Pt[]): number {
	if (poly.length < 3) return Infinity;
	const d = distToPolyline(x, y, poly, true);
	return pointInPolygon(x, y, poly) ? -d : +d;
}
